import { Box, Button, Dialog, List, ListItemButton, ListItemText, TextField, Typography } from '@mui/material'
import { DocumentType, DocumentTypesResponse } from '@src/types/documentPage'
import { useTranslation } from 'react-i18next'

interface ChangeDocumentTypeDialogProps {
  open: boolean
  selectedPagesCount: number
  documentTypesData?: DocumentTypesResponse
  productLine?: number
  selectedDocumentType: string
  docTypeSearch: string
  onClose: () => void
  onConfirm: () => void
  onReset: () => void
  onSelectType: (typeId: string) => void
  onSearchChange: (search: string) => void
  isLoading?: boolean
}

export const ChangeDocumentTypeDialog = ({
  open,
  selectedPagesCount,
  documentTypesData,
  productLine,
  selectedDocumentType,
  docTypeSearch,
  onClose,
  onConfirm,
  onReset,
  onSelectType,
  onSearchChange,
  isLoading,
}: ChangeDocumentTypeDialogProps) => {
  const { t, i18n } = useTranslation()

  const getFilteredDocTypes = (): DocumentType[] => {
    if (!documentTypesData || productLine === undefined || productLine > 1) return []

    const types = productLine === 0 ? documentTypesData.fa : documentTypesData.ifp
    if (!types) return []

    return types.filter((docType) => {
      const name = i18n.language === 'fr' ? docType.nameFr : docType.nameEn
      return name.toLowerCase().includes(docTypeSearch.toLowerCase())
    })
  }

  const filteredDocTypes = getFilteredDocTypes()

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('common.changeDocumentType')}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('common.selectedCount', { count: selectedPagesCount })}
        </Typography>

        {documentTypesData && productLine !== undefined && productLine <= 1 && (
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('common.searchDocumentType')}
              value={docTypeSearch}
              onChange={(e) => onSearchChange(e.target.value)}
              sx={{ mb: 2 }}
            />
            <List
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                maxHeight: 400,
                overflow: 'auto',
              }}
            >
              {filteredDocTypes.length > 0 ? (
                filteredDocTypes.map((docType) => (
                  <ListItemButton
                    key={`${productLine === 0 ? 'fa' : 'ifp'}-${docType.id}`}
                    selected={selectedDocumentType === String(docType.id)}
                    onClick={() => onSelectType(String(docType.id))}
                  >
                    <ListItemText primary={i18n.language === 'fr' ? docType.nameFr : docType.nameEn} />
                  </ListItemButton>
                ))
              ) : (
                <ListItemButton disabled>
                  <ListItemText primary={t('common.noResultsFound')} sx={{ textAlign: 'center' }} />
                </ListItemButton>
              )}
            </List>
          </Box>
        )}

        {productLine !== undefined && productLine > 1 && (
          <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
            {t('common.documentTypeChangeNotAvailable')}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
          <Button onClick={onReset} disabled={selectedDocumentType === ''}>
            {t('common.reset')}
          </Button>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button onClick={onClose}>{t('common.cancel')}</Button>
            <Button variant="contained" onClick={onConfirm} disabled={selectedDocumentType === '' || isLoading}>
              {isLoading ? t('common.savePending') : t('common.confirm')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  )
}
